// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete, apiPatch } from '../utils/apiClient';
import './SessionManager.css';

interface Session {
  id: string;
  session_name: string;
  pdf_fingerprint?: string;
  last_page?: number;
  total_interactions?: number;
  last_accessed_at: string;
}

interface SessionSelectPayload {
  id: string;
  name: string;
  pdf_fingerprint: string;
  last_page?: number;
  total_interactions?: number;
  isNew?: boolean;
}

interface SessionManagerProps {
  pdfFingerprint: string;
  onSessionSelect: (session: SessionSelectPayload) => void;
  onClose: () => void;
}

/**
 * 阅读会话管理组件
 * 支持创建、选择、删除、重命名会话
 */
export default function SessionManager({ pdfFingerprint, onSessionSelect, onClose }: SessionManagerProps) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [newSessionName, setNewSessionName] = useState<string>('');
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');

  // 加载会话列表
  useEffect(() => {
    loadSessions();
  }, [pdfFingerprint]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await apiGet<any>(`/api/reading-sessions/${pdfFingerprint}`);
      if (response.success) {
        setSessions(response.sessions || []);
      }
    } catch (error: any) {
      console.error('加载会话失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 创建新会话
  const handleCreateSession = async () => {
    const sessionName = newSessionName.trim() || '未命名会话';

    try {
      const response = await apiPost<any>('/api/reading-sessions', {
        pdfFingerprint,
        sessionName
      });

      if (response.success) {
        setNewSessionName('');
        await loadSessions();

        // 自动选择新创建的会话
        onSessionSelect({
          id: response.sessionId,
          name: sessionName,
          pdf_fingerprint: pdfFingerprint,
          isNew: true
        });
        onClose();
      } else {
        alert('创建会话失败: ' + (response.error || '未知错误'));
      }
    } catch (error: any) {
      console.error('创建会话失败:', error);
      alert('创建会话失败: ' + error.message);
    }
  };

  // 选择会话
  const handleSelectSession = (session: Session) => {
    onSessionSelect({
      id: session.id,
      name: session.session_name,
      pdf_fingerprint: session.pdf_fingerprint || pdfFingerprint,
      last_page: session.last_page,
      total_interactions: session.total_interactions
    });
    onClose();
  };

  // 删除会话
  const handleDeleteSession = async (sessionId: string, sessionName: string) => {
    if (!window.confirm(`确定要删除会话"${sessionName}"吗？\n\n注意：删除会话不会删除标注数据，只是清除会话历史。`)) {
      return;
    }

    try {
      const response = await apiDelete<any>(`/api/reading-sessions/${sessionId}`);
      if (response.success) {
        await loadSessions();
      } else {
        alert('删除失败: ' + (response.error || '未知错误'));
      }
    } catch (error: any) {
      console.error('删除会话失败:', error);
      alert('删除失败: ' + error.message);
    }
  };

  // 重命名会话
  const handleRenameSession = async (sessionId: string) => {
    const newName = editingName.trim();
    if (!newName) {
      alert('会话名称不能为空');
      return;
    }

    try {
      const response = await apiPatch<any>(`/api/reading-sessions/${sessionId}`, {
        sessionName: newName
      });

      if (response.success) {
        setEditingSessionId(null);
        setEditingName('');
        await loadSessions();
      } else {
        alert('重命名失败: ' + (response.error || '未知错误'));
      }
    } catch (error: any) {
      console.error('重命名会话失败:', error);
      alert('重命名失败: ' + error.message);
    }
  };

  // 格式化时间显示
  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    // 少于1分钟
    if (diff < 60000) return '刚刚';
    // 少于1小时
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
    // 少于1天
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
    // 少于7天
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`;

    // 超过7天显示日期
    return date.toLocaleDateString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="session-manager-overlay" onClick={onClose}>
      <div className="session-manager-modal" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="session-manager-header">
          <h2>阅读会话管理</h2>
          <button className="close-btn" onClick={onClose}>&#10005;</button>
        </div>

        <div className="session-manager-content">
          {/* 创建新会话 */}
          <div className="create-session-section">
            <h3>创建新会话</h3>
            <div className="create-session-form">
              <input
                type="text"
                placeholder="输入会话名称（可选）"
                value={newSessionName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSessionName(e.target.value)}
                onKeyPress={(e: React.KeyboardEvent) => e.key === 'Enter' && handleCreateSession()}
                maxLength={50}
              />
              <button onClick={handleCreateSession}>创建</button>
            </div>
            <p className="hint">开始全新的阅读理解会话，AI会记住你在这个会话中的所有交互</p>
          </div>

          {/* 历史会话列表 */}
          <div className="sessions-list-section">
            <h3>历史会话 ({sessions.length})</h3>

            {loading ? (
              <div className="loading">加载中...</div>
            ) : sessions.length === 0 ? (
              <div className="empty-sessions">
                <p>还没有会话，创建第一个会话开始吧！</p>
              </div>
            ) : (
              <div className="sessions-list">
                {sessions.map((session: Session) => (
                  <div key={session.id} className="session-item">
                    <div className="session-info">
                      {editingSessionId === session.id ? (
                        <div className="session-edit">
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingName(e.target.value)}
                            onKeyPress={(e: React.KeyboardEvent) => {
                              if (e.key === 'Enter') handleRenameSession(session.id);
                              if (e.key === 'Escape') {
                                setEditingSessionId(null);
                                setEditingName('');
                              }
                            }}
                            autoFocus
                          />
                          <button
                            className="save-btn"
                            onClick={() => handleRenameSession(session.id)}
                          >
                            保存
                          </button>
                          <button
                            className="cancel-btn"
                            onClick={() => {
                              setEditingSessionId(null);
                              setEditingName('');
                            }}
                          >
                            取消
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="session-name">{session.session_name}</div>
                          <div className="session-meta">
                            <span className="session-time">
                              {formatTime(session.last_accessed_at)}
                            </span>
                            <span className="session-stats">
                              {session.total_interactions}条记录
                            </span>
                            {session.last_page && (
                              <span className="session-page">
                                阅读至第{session.last_page}页
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="session-actions">
                      <button
                        className="continue-btn"
                        onClick={() => handleSelectSession(session)}
                      >
                        继续
                      </button>
                      <button
                        className="rename-btn"
                        onClick={() => {
                          setEditingSessionId(session.id);
                          setEditingName(session.session_name);
                        }}
                      >
                        重命名
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteSession(session.id, session.session_name)}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="session-manager-footer">
          <button className="cancel-btn-large" onClick={onClose}>取消</button>
        </div>
      </div>
    </div>
  );
}
